import * as signalR from '@microsoft/signalr';

class SignalRService {
    constructor() {
        this.connection = null;
        this.baseUrl = import.meta.env.VITE_API_BASE_URL;
        this.notificationCallbacks = [];
        this.unreadCount = 0;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isConnecting = false;
        this.reconnectDelay = 1000;
    }

    onNotification(callback) {
        this.notificationCallbacks.push(callback);
        return () => {
            this.notificationCallbacks = this.notificationCallbacks.filter(cb => cb !== callback);
        };
    }

    async connect(accountId) {
        // Empêcher les connexions multiples
        if (this.isConnecting) {
            return;
        }
        
        if (this.connection && this.isConnected) {
            return;
        }

        this.isConnecting = true;

        try {
            const hubUrl = `${this.baseUrl}/notificationHub`;

            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(hubUrl, {
                    accessTokenFactory: () => this.getAccessToken(),
                    withCredentials: true
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: retryContext => {
                        this.reconnectAttempts++;
                        if (this.reconnectAttempts > this.maxReconnectAttempts) {
                            return null;
                        }
                        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
                    }
                })
                .configureLogging(signalR.LogLevel.None)
                .build();

            // Enregistrer les listeners AVANT start()
            this.connection.on("ReceiveNotification", (notification) => {
                this.unreadCount++;
                
                this.notificationCallbacks.forEach(callback => {
                    callback(notification);
                });
            });

            this.connection.onreconnected(async () => {
                this.isConnected = true;
                
                // Se réenregistrer après reconnexion
                if (accountId) {
                    await this.registerUser(accountId);
                }
            });

            // Démarrer la connexion
            await this.connection.start();
            
            this.isConnected = true;
            this.reconnectAttempts = 0;

            // S'enregistrer IMMÉDIATEMENT après connexion
            if (accountId) {
                await this.registerUser(accountId);
            }

            return this.connection;
        } catch (error) {
            this.isConnected = false;
            this.isConnecting = false;
            
            if (error.message.includes('WebSocket') || error.message.includes('negotiate')) {
                await this.tryFallbackConnection(accountId);
            } else {
                throw error;
            }
        } finally {
            this.isConnecting = false;
        }
    }

    async tryFallbackConnection(accountId) {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(`${this.baseUrl}/notificationHub`, {
                    transport: signalR.HttpTransportType.LongPolling,
                    accessTokenFactory: () => this.getAccessToken(),
                    withCredentials: true
                })
                .withAutomaticReconnect()
                .configureLogging(signalR.LogLevel.None)
                .build();

            // Enregistrer les listeners avant start()
            this.connection.on("ReceiveNotification", (notification) => {
                this.unreadCount++;
                this.notificationCallbacks.forEach(callback => callback(notification));
            });

            await this.connection.start();
            this.isConnected = true;
            
            // S'enregistrer immédiatement
            if (accountId) {
                await this.registerUser(accountId);
            }
    }

    async tryReconnect(accountId) {
        // Attendre avant de reconnecter
        setTimeout(async () => {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                try {
                    await this.connect(accountId);
                } catch  {
                    // Augmenter le délai progressivement
                    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
                    this.tryReconnect(accountId);
                }
            }
        }, this.reconnectDelay);
    }

    async registerUser(accountId) {
        if (this.connection && this.isConnected) {
            try {
                await this.connection.invoke("RegisterUser", accountId.toString());
            } catch  {
                // throw ;
            }
        }
    }

    async joinValidatorGroup(accountId) {
        if (this.connection && this.isConnected) {
            try {
                await this.connection.invoke("JoinValidatorGroup", accountId.toString());
            } catch  {
                // Gestion silencieuse de l'erreur
            }
        }
    }

    async joinManagerGroup(accountId) {
        if (this.connection && this.isConnected) {
            try {
                await this.connection.invoke("JoinManagerGroup", accountId.toString());
            } catch  {
                // Gestion silencieuse de l'erreur
            }
        }
    }

    async markAsRead(notificationId, accountId) {
        if (this.connection && this.isConnected) {
            try {
                await this.connection.invoke("MarkAsRead", notificationId, accountId.toString());
                this.unreadCount = Math.max(0, this.unreadCount - 1);
            } catch  {
                // Gestion silencieuse de l'erreur
            }
        }
    }

    async disconnect() {
        if (this.connection) {
            try {
                await this.connection.stop();
                this.isConnected = false;
                this.connection = null;
            } catch  {
                // Gestion silencieuse de l'erreur
            }
        }
    }

    getAccessToken() {
        const token = localStorage.getItem('clerk_session');
        return token || '';
    }

    getUnreadCount() {
        return this.unreadCount;
    }

    resetUnreadCount() {
        this.unreadCount = 0;
    }

    updateUnreadCount(count) {
        this.unreadCount = count;
    }
}

const signalRService = new SignalRService();
export default signalRService;